// src/components/portal/FunderRegister.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
createUserWithEmailAndPassword,
sendEmailVerification,
deleteUser,
} from "firebase/auth";
import { auth, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import tpcLogo from "../../images/TPC-logo-light.png";
import "./Portalauth.css";

const CONFIG = {
company: "TPC_Group",
collectionBrand: "TechPerformanceCentre",
loginRoute: "/funder-login",
dashboardRoute: "/funder-dashboard",
roleName: "Funder",
};

export default function FunderRegister() {
const [email, setEmail]         = useState("");
const [funderName, setFunderName] = useState("");
const [contactName, setContactName] = useState("");
const [phone, setPhone] = useState("");
const [pw, setPw]               = useState("");
const [confirmPw, setConfirmPw] = useState("");
const [terms, setTerms]         = useState(false);
const [showPw, setShowPw]       = useState(false);
const [showCPw, setShowCPw]     = useState(false);
const [busy, setBusy]           = useState(false);
const [err, setErr]             = useState("");
const [verifyStep, setVerify]   = useState(false);
const [inviteLoading, setInviteLoading] = useState(true);
const [invite, setInvite] = useState(null);
const [inviteToken, setInviteToken] = useState("");

const nav = useNavigate();
const location = useLocation();
const firestoreOperations = httpsCallable(functions, "firestoreOperations");

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
const buildInviteFromUrlParams = (params, token) => {
    const emailFromUrl = normalizeEmail(params.get("email"));
    const funderIdFromUrl = String(params.get("funderId") || "").trim();
    if (!emailFromUrl || !funderIdFromUrl) return null;
    const expiresAt = String(params.get("expiresAt") || "").trim();
    const expiresMs = Date.parse(expiresAt);
    if (Number.isFinite(expiresMs) && expiresMs <= Date.now()) {
    throw new Error("This funder registration link has expired.");
    }
    return {
    token,
    email: emailFromUrl,
    funderId: funderIdFromUrl,
    funderName: String(params.get("funderName") || "").trim(),
    contactOfficerName: String(params.get("contactName") || "").trim(),
    contactOfficerNumber: String(params.get("phone") || "").trim(),
    expiresAt,
    fromUrlParams: true,
    };
};

const buildLegacyInviteFromToken = (token) => ({
    token,
    email: "",
    funderId: "",
    funderName: "",
    contactOfficerName: "",
    contactOfficerNumber: "",
    fromLegacyToken: true,
});

useEffect(() => {
let cancelled = false;
const loadInvite = async () => {
    setInviteLoading(true);
    setErr("");
    const params = new URLSearchParams(location.search || "");
    const token = String(params.get("token") || "").trim();
    setInviteToken(token);
    if (!token) {
    setInvite(null);
    setErr("Funder registration is invite-only. Please use the unique registration link sent to your email.");
    setInviteLoading(false);
    return;
    }
    try {
    const inviteDoc = buildInviteFromUrlParams(params, token) || buildLegacyInviteFromToken(token);
    if (!cancelled) {
        const safeEmail = normalizeEmail(inviteDoc.email);
        setInvite(inviteDoc);
        setEmail(safeEmail);
        setFunderName(inviteDoc.funderName || "");
        setContactName(inviteDoc.contactOfficerName || "");
        setPhone(inviteDoc.contactOfficerNumber || "");
        setErr("");
    }
    } catch (ex) {
    if (!cancelled) {
        setInvite(buildLegacyInviteFromToken(token));
        setEmail("");
        setFunderName("");
        setContactName("");
        setPhone("");
        setErr("");
    }
    } finally {
    if (!cancelled) setInviteLoading(false);
    }
};
loadInvite();
return () => { cancelled = true; };
}, [location.search]);

const handleRegister = async (e) => {
e.preventDefault();
setErr("");
if (!invite || !inviteToken) return setErr("Please use the unique funder registration link sent to your email.");
if (!terms)            return setErr("Please accept Terms & Privacy.");
if (!email || !funderName.trim() || !contactName.trim() || !pw || !confirmPw) return setErr("All required fields must be completed.");
if (pw.length < 6)     return setErr("Password must be 6+ characters.");
if (pw !== confirmPw)  return setErr("Passwords do not match.");

setBusy(true);
let firebaseUser = null;
try {
    const safeEmail = normalizeEmail(email);
    const inviteEmail = normalizeEmail(invite.email);
    if (inviteEmail && safeEmail !== inviteEmail) throw new Error("This invite is not valid for the email address entered.");

    const { user } = await createUserWithEmailAndPassword(auth, email.trim(), pw);
    firebaseUser = user;

    await sendEmailVerification(user);

    let accepted = null;
    if (invite.funderId) {
    const nowIso = new Date().toISOString();
    let existingFunder = {};
    try {
        const readResult = await firestoreOperations({
        operation: "read",
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: "funders",
        uniqueId: invite.funderId,
        auth: true,
        });
        existingFunder = readResult?.data?.data || {};
    } catch (_) {
        existingFunder = {};
    }
    const primaryContactEmail = normalizeEmail(existingFunder.ContactOfficerEmail || existingFunder.contactOfficerEmail || "");
    const isPrimaryContact = primaryContactEmail && primaryContactEmail === safeEmail;
    const userEntry = {
        email: safeEmail,
        name: contactName.trim(),
        phone: phone.trim(),
        authUid: user.uid,
        registeredAt: nowIso,
        status: "active",
    };
    const existingPortalUsers = Array.isArray(existingFunder.portalUsers) ? existingFunder.portalUsers : [];
    const nextPortalUsers = [
        ...existingPortalUsers.filter((portalUser) => normalizeEmail(portalUser?.email || portalUser) !== safeEmail),
        userEntry,
    ];
    const funderUpdate = {
        portalUsers: nextPortalUsers,
        updatedAt: nowIso,
    };
    if (isPrimaryContact || !primaryContactEmail) {
        Object.assign(funderUpdate, {
        authUid: user.uid,
        registeredEmail: safeEmail,
        registeredAt: nowIso,
        ContactOfficerEmail: safeEmail,
        ContactOfficerName: contactName.trim(),
        ContactOfficerNumber: phone.trim(),
        });
    }
    await firestoreOperations({
        operation: "update",
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: "funders",
        uniqueId: invite.funderId,
        documentData: funderUpdate,
        auth: true,
    });
    await firestoreOperations({
        operation: "update",
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: "funderPortalInvites",
        uniqueId: inviteToken,
        documentData: {
        status: "used",
        usedAt: nowIso,
        usedByAuthUid: user.uid,
        registeredEmail: safeEmail,
        },
        auth: true,
    });
    accepted = { data: { funderId: invite.funderId } };
    } else {
    accepted = { data: { funderId: "" } };
    }

    sessionStorage.setItem("portalRole", "funder");
    sessionStorage.setItem("portalEmail", safeEmail);
    sessionStorage.setItem("portalFunderId", accepted?.data?.funderId || invite.funderId || "");

    setVerify(true);
} catch (ex) {
    // If we created an Auth account but something else failed, clean it up
    if (firebaseUser) {
    try { await deleteUser(firebaseUser); } catch (_) { /* ignore */ }
    }
    if (ex.code === "auth/email-already-in-use") {
    setErr("An account with this email already exists. Please log in instead.");
    } else if (ex.code === "auth/invalid-email") {
    setErr("Please enter a valid email address.");
    } else {
    setErr(ex.message || "Registration failed. Please try again.");
    }
} finally {
    setBusy(false);
}
};

// Eye icon helper
const EyeIcon = ({ open }) => open ? (
<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
</svg>
) : (
<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
</svg>
);

return (
<div className="ar-container">
    {/* Left banner — matches ApplicantRegisterManual exactly */}
    <div className="ar-left">
    <div className="ar-logo-box">
        <img src={tpcLogo} alt="TPC" className="ar-logo" />
    </div>
    <div className="ar-banner-text">Funder Portal</div>
    </div>

    {/* Right pane */}
    <div className="ar-right">
    {verifyStep ? (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"60px 40px", textAlign:"center", background:"linear-gradient(135deg,#f8fafc 0%,#fff 100%)", borderRadius:"16px", boxShadow:"0 4px 20px rgba(0,0,0,0.08)", border:"1px solid #e5e7eb" }}>
        <div style={{ width:"80px", height:"80px", borderRadius:"50%", background:"linear-gradient(135deg,#3b82f6 0%,#2563eb 100%)", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:"24px" }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
            </svg>
        </div>
        <h3 style={{ color:"#1f3864", marginBottom:"12px" }}>Verify your email</h3>
        <p style={{ fontSize:"15px", color:"#6b7280", lineHeight:"1.6", margin:"0 0 16px 0", maxWidth:"400px" }}>
            We've sent a verification link to <strong>{email}</strong>.<br/>
            Please check your inbox and click the link to activate your Funder account.
            <span style={{ fontSize:"14px", color:"#1e3a8a", fontWeight:"600", backgroundColor:"#dbeafe", padding:"8px 12px", borderRadius:"6px", display:"inline-block", marginTop:"12px" }}>
            💡 Don't forget to check your spam folder!
            </span>
        </p>
        <button
            className="ar-button"
            style={{ maxWidth:"240px", marginTop:"8px" }}
            onClick={() => nav(CONFIG.loginRoute)}
        >
            Go to Funder Login
        </button>
        </div>
    ) : inviteLoading ? (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"60px 40px", textAlign:"center" }}>
        <div className="ar-spinner" />
        <h3 style={{ color:"#1f3864", marginTop:"18px" }}>Checking invite...</h3>
        </div>
    ) : !invite ? (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"60px 40px", textAlign:"center", background:"#fff", borderRadius:"16px", border:"1px solid #e5e7eb" }}>
        <h3 style={{ color:"#1f3864", marginBottom:"12px" }}>Invite required</h3>
        {err && <p className="ar-err" style={{ maxWidth:"460px" }}>{err}</p>}
        <p style={{ color:"#6b7280", lineHeight:"1.6", maxWidth:"460px" }}>
            Funder registration is now invite-only. Please use the unique registration link sent to your email address.
        </p>
        <button className="ar-button" style={{ maxWidth:"240px" }} onClick={() => nav(CONFIG.loginRoute)}>Go to Funder Login</button>
        </div>
    ) : (
        <form className="ar-form" onSubmit={handleRegister}>
        <h2 className="ar-title">Create Funder Account</h2>
        {err && <p className="ar-err">{err}</p>}

        <input
            type="text"
            placeholder="Funder / Company Name"
            className="ar-input"
            value={funderName}
            onChange={(e) => setFunderName(e.target.value)}
            required
        />

        <input
            type="text"
            placeholder="Contact Officer Name"
            className="ar-input"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            required
        />

        <input
            type="email"
            placeholder="Email"
            className="ar-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            readOnly={Boolean(invite.email)}
            required
        />

        <input
            type="tel"
            placeholder="Phone Number"
            className="ar-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
        />

        {/* Password with toggle */}
        <div style={{ position:"relative", width:"100%" }}>
            <input
            type={showPw ? "text" : "password"}
            placeholder="Enter your password"
            className="ar-input"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            required
            style={{ paddingRight:"45px" }}
            />
            <button type="button" onClick={() => setShowPw(!showPw)}
            style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:"4px", display:"flex", alignItems:"center", color:"#6b7280" }}>
            <EyeIcon open={showPw} />
            </button>
        </div>

        {/* Confirm password with toggle */}
        <div style={{ position:"relative", width:"100%" }}>
            <input
            type={showCPw ? "text" : "password"}
            placeholder="Confirm your password"
            className="ar-input"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            style={{ paddingRight:"45px" }}
            />
            <button type="button" onClick={() => setShowCPw(!showCPw)}
            style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:"4px", display:"flex", alignItems:"center", color:"#6b7280" }}>
            <EyeIcon open={showCPw} />
            </button>
        </div>

        <div className="ar-check-wrap">
            <input
            type="checkbox"
            id="terms-cb"
            className="ar-check"
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
            required
            />
            <label htmlFor="terms-cb" className="ar-check-label" style={{ cursor:"pointer" }}>
            I agree to&nbsp;
            <a href="https://techperformancecentre.com/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms</a>
            &nbsp;and&nbsp;
            <a href="https://techperformancecentre.com/terms-and-conditions" target="_blank" rel="noopener noreferrer">Privacy</a>.
            </label>
        </div>

        <div style={{ maxHeight: !terms ? "50px" : "0", overflow:"hidden", transition:"max-height 0.4s cubic-bezier(0.4,0,0.2,1), margin 0.4s", marginBottom: !terms ? "10px" : "0", opacity: !terms ? 1 : 0 }}>
            <p style={{ color:"#f59e0b", fontSize:"0.85rem", textAlign:"center", margin:"0", fontWeight:"500" }}>
            ⚠️ Please accept Terms &amp; Privacy to continue
            </p>
        </div>

        <button type="submit" className="ar-button" disabled={!terms || busy}>
            {busy ? (
            <div className="ar-button-loader">
                <div className="ar-spinner" />
                <span>Creating account…</span>
            </div>
            ) : "Create Funder Account"}
        </button>

        <p className="ar-small">
            Already have an account?&nbsp;<Link to={CONFIG.loginRoute}>Log in</Link>
        </p>

        <p className="ar-small" style={{ marginTop:"8px" }}>
            <Link to="/" style={{ color:"#9ca3af" }}>← Back to home</Link>
        </p>
        </form>
    )}
    </div>
</div>
);
}
